// utils.ts

import * as Yup from 'yup';
import * as Sentry from '@sentry/nextjs';
import { flushSync } from 'react-dom';
import { submitLeadFormData } from '@/lib/lead-submission';
// Build initial form values with hidden fields
export const buildInitialValues = (
  hiddenFields: Array<{ key: string; value: string }> = [],
  defaultValues: any,
  radioGroups: Array<{ name?: string }> = [],
  select: Array<{ name?: string }> = [],
  checkboxGroups: Array<{ name?: string }> = [],
  textInputs: Array<{ name?: string }> = [],
) => {
  return {
    ...defaultValues,
    ...Object.fromEntries(
      hiddenFields.map((field) => [field.key, field.value]),
    ),
    ...Object.fromEntries(radioGroups.map((group) => [group.name ?? '', ''])),
    ...Object.fromEntries(select.map((select) => [select.name ?? '', ''])),
    ...Object.fromEntries(
      checkboxGroups.map((group) => [group.name ?? '', []]),
    ),
    ...Object.fromEntries(
      textInputs.map((group) => [group.name ?? 'MISSING_NAME', '']),
    ), // Debugging
  };
};

export const getPhoneValidation = (t: (k: string) => string) =>
  Yup.string()
    .transform((v) => (v ?? '').replace(/\s+/g, '')) // allow spaces
    .test('is-valid-phone', t('forms.validation.phone_invalid'), (value) => {
      if (!value) return false;
      const swissNumberPattern = /^(\+41|41)\d{9}$/;
      if (!swissNumberPattern.test(value)) return false;
      const swissMobilePrefixes = /^(\+417[5-9]|417[5-9])/;
      if (!swissMobilePrefixes.test(value)) return false;
      if (/(.)\1{4,}/.test(value)) return false;
      const sequentialPattern =
        /(?:01234|12345|23456|34567|45678|56789|98765|87654|76543|65432|54321|43210)/;
      if (sequentialPattern.test(value)) return false;
      return true;
    })
    .required(t('forms.validation.phone_require'));

// ---------- Email validation ----------
export const getEmailValidation = (t: (k: string) => string) =>
  Yup.string()
    .email(t('forms.validation.email_invalid'))
    .matches(
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
      t('forms.validation.emailInvalidDomain'),
    )
    .required(t('forms.validation.email_require'));

// ---------- Schema factory ----------
export const createValidationSchema = (
  radioGroupOptions: any[],
  selectGroup: any[],
  checkboxGroup: any[],
  textInputs: any[],
  t: (key: string) => string,
  options?: {
    requireSeasonAndProfession?: boolean;
    requireConsent?: boolean;
  },
) => {
  const baseSchema: Record<string, any> = {
    name: Yup.string().required(t('forms.validation.firstname_require')),
    surname: Yup.string().required(t('forms.validation.lastname_require')),
    email: getEmailValidation(t),
    phoneNumber: getPhoneValidation(t),
    zip: Yup.string().required(t('forms.validation.zip_require')),
    city: Yup.string().required(t('forms.validation.city_require')),
    gender: Yup.string().required(t('forms.validation.gender_require')),
    strasseNummer: Yup.string().required(t('forms.validation.street_require')),
  };

  if (options?.requireSeasonAndProfession !== false) {
    baseSchema.seasonYear = Yup.string().required(
      t('forms.validation.season_require'),
    );

    baseSchema.profession = Yup.string().required(
      t('forms.validation.profession_require'),
    );
  }

  // Consent is a client-side gate only (not transmitted to the CRM); it is used
  // by the form-gated spin wheel to require agreement before capturing a lead.
  if (options?.requireConsent) {
    baseSchema.consent = Yup.boolean().oneOf(
      [true],
      t('gatewayForms.pleaseAgreement'),
    );
  }

  // ---------- Dynamic groups ----------
  radioGroupOptions.forEach((group) => {
    const options = group.options.map((option: any) => option.value);
    baseSchema[group.name] = Yup.string()
      .oneOf(options, `Bitte wählen Sie eine gültige Option.`)
      .required(`Bitte wählen Sie eine Option.`);
  });

  selectGroup.forEach((group) => {
    const options = group.options.map((option: any) => option.value);
    baseSchema[group.name] = Yup.string()
      .oneOf(options, `Bitte wählen Sie eine gültige Option.`)
      .required(`Bitte wählen Sie eine Option.`);
  });

  checkboxGroup.forEach((group) => {
    baseSchema[group.name] = Yup.array()
      .min(1, `Bitte wählen Sie mindestens eine Option.`)
      .of(Yup.string().required(`Bitte wählen Sie eine gültige Option.`));
  });

  textInputs.forEach((input) => {
    baseSchema[input.name] = Yup.string().required(
      `${input.label || 'Feld'} ist ein Pflichtfeld.`,
    );
  });

  return Yup.object(baseSchema);
};

// Generate season years for dropdown
export const generateSeasonYears = () =>
  Array.from({ length: 5 }, (_, i) => new Date().getFullYear() + i);

// Submit form data to API
export const submitFormData = async (
  values: any,
  radioGroups: any[] = [],
  selectGroups: any[] = [],
  checkboxGroups: any[] = [],
  textInputs: any[] = [],
  hiddenFields: Array<{ key: string; value: string }> = [],
  options?: { extraCallbackNotes?: string },
) => {
  const formData = new FormData();

  const radioGroupNotes = radioGroups
    .map((group) => ({
      label: group.label,
      value: values[group.name],
    }))
    .filter((item) => item.value)
    .map((item) => `${item.label}: ${item.value}`);

  const selectGroupNotes = selectGroups
    .map((group) => ({
      label: group.label,
      value: values[group.name],
    }))
    .filter((item) => item.value)
    .map((item) => `${item.label}: ${item.value}`);

  const checkboxGroupNotes = checkboxGroups
    .map((group) => ({
      label: group.label,
      values: values[group.name] || [],
    }))
    .filter((group) => group.values.length > 0)
    .map(
      (group) =>
        `${group.label}:\n${group.values.map((val: string) => `• ${val}`).join('\n')}`,
    );

  const textInputsNotes = textInputs
    .map((input) => ({
      label: input.label,
      value: values[input.name],
    }))
    .filter((item) => item.value)
    .map((item) => `${item.label}: ${item.value}`);

  const callbackRequestNotes = [
    ...radioGroupNotes,
    ...selectGroupNotes,
    ...checkboxGroupNotes,
    ...textInputsNotes,
    ...(options?.extraCallbackNotes ? [options.extraCallbackNotes] : []),
  ]
    .filter(Boolean)
    .join('\n');

  const { sourceUrl, sourceKey } = getUtmSourceData();
  const hiddenFieldMap = Object.fromEntries(
    hiddenFields.map((field) => [field.key, field.value]),
  );
  const utmParameter =
    sourceKey ||
    hiddenFieldMap.UTM_Parameter__c ||
    'Strapi parameter not configured';

  formData.append('Salutation', values.gender === 'Male' ? 'Herr' : 'Frau');
  formData.append('LastName', values.surname);
  formData.append('FirstName', values.name);
  formData.append('Email', values.email);
  formData.append('MobilePhone', values.phoneNumber);
  formData.append('nx_Berufsbezeichnung__c', values.profession);
  formData.append('Street', values.strasseNummer);
  formData.append('City', values.city);
  formData.append('CallbackRequestNotes__c', callbackRequestNotes);
  formData.append('PostalCode', values.zip);
  formData.append('UTM_Parameter__c', utmParameter);
  formData.append('Tech_Data_Origin__c', 'W2L');
  formData.append('LeadSource', 'Website');
  formData.append('Source_URL__c', sourceUrl || 'https://smzh.ch/de');
  formData.append('nx_Jahrgang__c', values.seasonYear);
  formData.append('Add_to_Campaign__c', values.Add_to_Campaign__c ?? false);
  formData.append('preferredLanguage', getPreferredLanguage());

  const knownKeys = new Set([
    'Salutation',
    'LastName',
    'FirstName',
    'Email',
    'MobilePhone',
    'nx_Berufsbezeichnung__c',
    'Street',
    'City',
    'CallbackRequestNotes__c',
    'PostalCode',
    'UTM_Parameter__c',
    'Tech_Data_Origin__c',
    'LeadSource',
    'Source_URL__c',
    'nx_Jahrgang__c',
    'Add_to_Campaign__c',
  ]);

  hiddenFields.forEach(({ key, value }) => {
    if (!knownKeys.has(key)) {
      formData.append(key, value);
    }
  });

  try {
    const response = await submitLeadFormData(formData);

    return response.ok;
  } catch (error) {
    console.error('Form submission error:', error);
    Sentry.captureException(error);
    return false;
  }
};

export const getUtmSourceData = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const sourceKey = urlParams.get('utm_sourcekey') || '';
  const sourceUrl = window.location.href || 'https://smzh.ch/de/';
  return { sourceKey, sourceUrl };
};

// Resolves the form submitter's preferred language from the current URL.
// `/en/...` → "Englisch", anything else (incl. `/de/...`, no locale, SSR) → "Deutsch".
export const getPreferredLanguage = (): 'Deutsch' | 'Englisch' => {
  if (typeof window === 'undefined') return 'Deutsch';
  const firstSegment = window.location.pathname
    .split('/')
    .filter(Boolean)[0]
    ?.toLowerCase();
  return firstSegment === 'en' ? 'Englisch' : 'Deutsch';
};

export const resolveGatewayRedirectHref = (
  redirectionLink: string | null | undefined,
  locale: string,
) => {
  const trimmedLink = redirectionLink?.trim();

  if (!trimmedLink) {
    return null;
  }

  const normalizedLink = trimmedLink.replace(/^\/+|\/+$/g, '');
  const normalizedLowerLink = normalizedLink.toLowerCase();
  const homeAliases = new Set(['home']);

  if (!normalizedLink) {
    return `/${locale}`;
  }

  if (homeAliases.has(normalizedLowerLink)) {
    return `/${locale}`;
  }

  if (
    normalizedLowerLink === locale ||
    normalizedLowerLink.startsWith(`${locale}/`) ||
    normalizedLowerLink.startsWith('de/') ||
    normalizedLowerLink.startsWith('en/')
  ) {
    return `/${normalizedLink}`;
  }

  return `/${locale}/${normalizedLink}`;
};

export const getUtmPhotoLink = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('utm_photoLink');
};

export const showGatewaySuccessModal = (
  setIsSubmitted: (isSubmitted: boolean) => void,
  afterPaint?: () => void,
) => {
  flushSync(() => {
    setIsSubmitted(true);
  });

  if (afterPaint) {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(afterPaint);
    });
  }
};
